// Static data for the portfolio — source of truth: public/Veeramalli_Sathwik_Resume.pdf

export const personalInfo = {
    name: 'Sathwik Veeramalli',
    email: 'veeramallisathwik@gmail.com',
    phone: '+91 9676214660',
    title: 'Senior Software Engineer | React, TypeScript & .NET/Azure',
    role: 'Senior Software Engineer',
    company: 'Bosch',
    tagline:
        'Senior engineer owning frontend architecture and shipping across the stack at Bosch',
    college: 'Indian Institute of Technology Hyderabad',
    degree: 'B.Tech in Computer Science',
    cgpa: '8.21/10',
    graduationYear: '2023',
    educationPeriod: '2019 – 2023',
    experienceSince: 'August 2023',
    location: 'Hyderabad, India',
    linkedin: 'https://www.linkedin.com/in/veeramalli-sathwik-43951423b',
    github: 'https://github.com/sathwik22',
    resumePath: '/Veeramalli_Sathwik_Resume.pdf',
};

export const heroLines = [
    "Hi, I'm Sathwik Veeramalli.",
    'Senior Software Engineer at Bosch.',
    'React, TypeScript & .NET/Azure.',
    'Building for 200K+ users.',
    'Leading GenAI adoption.',
];

export const heroRoles = [
    'Senior Software Engineer @ Bosch',
    'Frontend Architect',
    'React · Redux · TypeScript',
    '.NET Microservices · Redis · Azure',
    'GenAI Adoption Lead',
];

export const heroLead =
    'I own frontend architecture for a multi-tenant e-commerce platform serving 200K+ users across 15 countries — and I go deep into .NET microservices, Redis and Azure to ship fixes end to end.';

export const stats = [
    { value: '200K+', label: 'Users served' },
    { value: '$30M+', label: 'Annual transactions' },
    { value: '1.8s', label: 'LCP (from 3.2s)' },
    { value: '$180K', label: 'Yearly infra savings' },
];

export const aboutDescription =
    'Senior Software Engineer at Bosch Global Software Technologies with 3+ years building high-traffic React platforms. I own the frontend architecture of a multi-tenant e-commerce product on a .NET microservices / Azure backend, built the team’s testing culture from zero to 90% coverage, and am the go-to engineer for production incidents across React, .NET, Redis and Azure. I also lead GenAI adoption training for a 250+ engineer department.';

export const awards = [
    {
        title: 'Inspirer · Champ Skill · Kudos Awards',
        detail: 'Bosch — AI adoption leadership, L2 React competency and rapid cross-stack delivery',
    },
    {
        title: 'JEE Advanced AIR 834 · JEE Mains AIR 915',
        detail: 'Among 1.2M+ candidates',
    },
];

export const experienceItems = [
    {
        period: 'Aug 2023 – Present',
        title: 'Software Engineer → Senior Software Engineer',
        company: 'Bosch Global Software Technologies',
        location: 'Hyderabad',
        achievements: [
            'Own frontend architecture for a multi-tenant e-commerce platform (200K+ users, 15 countries, $30M+ annual transactions) on a .NET microservices/Azure backend; recent work lifted conversion by roughly 12%.',
            'Built the team’s testing culture from zero, growing Jest/React Testing Library coverage to 90% and cutting regressions reaching production.',
            'Led a security remediation effort closing 300+ vulnerabilities (critical/high/medium) and added automated dependency scanning to CI/CD.',
            'Refactored the Redux data layer with lazy loading and code splitting, dropping LCP from 3.2s to 1.8s.',
            'Go-to engineer for production incidents across React, .NET microservices, Redis and Azure; fast root-cause isolation cut average time-to-fix by roughly 45%.',
            'Migrated the caching layer off Memcached onto Azure Managed Redis, simplifying the architecture and cutting infra spend by about $180K/year.',
            'Built a customizable React/React-Admin dashboard for 30K concurrent users with personalization features, maintained at 99.2% uptime with full Cypress coverage.',
            'Moved state management from React Context to Redux to unblock stalled feature work; the resulting component library was later adopted by two other teams.',
            'Mentored interns on an AI chatbot assistant and a campus wayfinding app, and led GenAI adoption training for a 250+ engineer department; recognized with Bosch’s Inspirer Award.',
        ],
    },
];

export const skillGroups = [
    {
        title: 'Frontend',
        accent: 'cyan',
        items: ['React.js', 'Redux', 'TypeScript', 'JavaScript', 'HTML5', 'CSS3', 'Material UI', 'React-Admin', 'Performance Optimization'],
    },
    {
        title: 'Backend',
        accent: 'violet',
        items: ['Python (FastAPI, Django)', '.NET Microservices', 'REST APIs', 'Redis', 'PostgreSQL'],
    },
    {
        title: 'Cloud & DevOps',
        accent: 'pink',
        items: ['Azure App Service', 'Azure Managed Redis', 'CI/CD (GitHub Actions)', 'Docker', 'Linux'],
    },
    {
        title: 'Testing & Quality',
        accent: 'cyan',
        items: ['Jest', 'React Testing Library', 'Cypress', 'End-to-End Testing'],
    },
    {
        title: 'Security',
        accent: 'violet',
        items: ['SCA (Grype, Trivy)', 'Dependency Scanning', 'Vulnerability Remediation'],
    },
    {
        title: 'Engineering',
        accent: 'pink',
        items: ['System Architecture', 'Debugging / Troubleshooting', 'Git', 'GenAI Adoption', 'Mentoring'],
    },
];

export const skills = skillGroups.flatMap((group) =>
    group.items.map((name) => ({ name, level: 90 }))
);

export const projectData = [
    {
        title: 'Multi-Tenant E-Commerce Platform',
        description:
            'Own the frontend architecture of a multi-tenant e-commerce platform on a .NET microservices / Azure backend. Refactored the Redux data layer with lazy loading and code splitting, migrated caching from Memcached to Azure Managed Redis, and built a testing culture from zero to 90% coverage.',
        technologies: ['React', 'TypeScript', 'Redux', '.NET Microservices', 'Azure', 'Redis', 'Jest'],
        image: '/ford-rotunda.PNG',
        demo: '#',
        highlights: [
            '200K+ users · 15 countries',
            '$30M+ annual transactions',
            'LCP 3.2s → 1.8s',
            '~$180K/year infra savings',
        ],
    },
    {
        title: 'AI-Powered Chat Interface with Feedback System',
        description:
            'Interactive chat UI with React + Redux for session/history management — create, rename and delete chats with local-storage persistence, backed by FastAPI. A companion React dashboard visualizes real-time feedback analytics stored in Firebase.',
        technologies: ['React', 'Redux', 'FastAPI', 'Firebase', 'Python'],
        image: '',
        demo: '#',
        // PLACEHOLDER: replace with this project's repository URL
        github: 'https://github.com/sathwik22',
        highlights: ['AI accuracy 64% → 89%', '1,000+ labeled interactions'],
    },
    {
        title: 'Personalized React-Admin Dashboard',
        description:
            'Customizable React/React-Admin dashboard with personalization features for 30K concurrent users, maintained at 99.2% uptime with full Cypress end-to-end coverage.',
        technologies: ['React', 'React-Admin', 'Material UI', 'Cypress'],
        image: '/rexroth.PNG',
        demo: '#',
        highlights: ['30K concurrent users', '99.2% uptime'],
    },
    {
        title: 'Security Vulnerability Report Viewer',
        description:
            'Hackathon-built dashboard unifying Trivy, Grype and other scanner outputs with smart filtering and severity-based prioritization — the same SCA expertise behind closing 300+ vulnerabilities at work.',
        technologies: ['React', 'Material UI', 'DevSecOps', 'Grype', 'Trivy'],
        image: '',
        demo: 'https://hackathon-semantic.vercel.app/',
        github: 'https://github.com/sathwik22/hackathon-semantic',
    },
    {
        title: 'Department Fund Management System',
        description:
            'Full-stack React + Django REST Framework app on PostgreSQL with role-based access control, multi-level approval workflow and real-time fund-utilization dashboards.',
        technologies: ['React', 'Django', 'PostgreSQL', 'REST API', 'Python'],
        image: '',
        demo: '#',
        github: 'https://github.com/sathwik22/DDF-Management-System',
    },
];
